import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { UserTier, User } from "@shared/schema";
export type { UserTier, User };
import { FREE_TIER_LIMITS, PRO_TIER_LIMITS } from "@shared/schema";
import { supabase } from "./supabase";
import type { SupabaseClient } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  tier: UserTier;
  setTier: (tier: UserTier) => void;
  isLoggedIn: boolean;
  canCreateRoute: boolean;
  canPost: boolean;
  canUseHomeFeature: boolean;
  isInsider: boolean;
  remainingRoutes: number;
  maxStopsPerRoute: number;
  remainingPosts: number;
  claimShop: (shopId: string) => void;
  unclaimShop: (shopId: string) => void;
  isShopOwner: (shopId: string) => boolean;
  getClaimPrice: (shopCount: number) => number;
  setHomeAddress: (address: string, details?: { city?: string; postalCode?: string; lat?: number; lng?: number }) => Promise<void>;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);
const AUTH_SYNC_COOLDOWN_MS = 60_000;
const AUTH_VERBOSE_LOGS = false;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    // Synchronous initial check from localStorage for instant UI
    if (typeof window !== 'undefined') {
      try {
        // Try the new key first
        const savedProfile = localStorage.getItem('vintmap-user-profile');
        if (savedProfile) {
          return JSON.parse(savedProfile);
        }

        // Fallback to the old way (session object might have fullUser)
        const savedSession = localStorage.getItem('vintmap-session');
        if (savedSession) {
          const session = JSON.parse(savedSession);
          if (session?.fullUser) {
            return session.fullUser;
          }
          if (session?.user) {
            const metaTier = session.user.user_metadata?.tier;
            const isPower = metaTier === 'power';
            const isActuallyInsider = isPower || (session.user.user_metadata?.is_insider || metaTier === "pro") || false;
            return {
              id: session.user.id,
              email: session.user.email || "",
              username: session.user.user_metadata?.username || session.user.email?.split("@")[0] || "gebruiker",
              tier: metaTier === "power" ? "power" : (isActuallyInsider ? "pro" : (metaTier || "free")),
              role: session.user.user_metadata?.role || "user",
              claimedShopIds: [],
              routeCount: 0,
              postsThisWeek: 0,
              homeAddress: undefined,
              avatarUrl: session.user.user_metadata?.avatar_url || null,
              isInsider: isActuallyInsider
            };
          }
        }
      } catch (e) {
        console.warn("[AUTH] Sync storage check failed", e);
      }
    }
    return null;
  });

  const [tier, setTierState] = useState<UserTier>(() => {
    return user?.tier || "anonymous";
  });
  const [loading, setLoading] = useState(() => {
    // If we found a user in localStorage, don't show the initial loader
    if (typeof window !== 'undefined' && localStorage.getItem('vintmap-session')) {
      return false;
    }
    return true;
  });

  // Fetch user data from backend using Supabase user id + access token
  const fetchUserData = useCallback(
    async (supabaseUserId: string, accessToken: string): Promise<void> => {
      // Cooldown + in-flight guard to prevent noisy repeated sync calls.
      const now = Date.now();
      const lastFetch = (window as any).__lastFetchAuth || 0;
      const inFlight = (window as any).__authFetchInFlight || false;
      if (inFlight) {
        return;
      }
      if (now - lastFetch < AUTH_SYNC_COOLDOWN_MS) {
        return;
      }
      (window as any).__lastFetchAuth = now;
      (window as any).__authFetchInFlight = true;

      try {
        const syncRes = await fetch("/api/users/me", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (AUTH_VERBOSE_LOGS) console.log("[AUTH] /api/users/me", syncRes.status);

        if (!syncRes.ok) {
          const errorData = await syncRes.json().catch(() => ({ error: "Unknown error" }));
          console.error("[AUTH] Failed to sync user to database:", errorData);
          // Only reset if it's a hard error like 401
          if (syncRes.status === 401) {
            setUser(null);
            setTierState("anonymous");
          }
          return;
        }

        const payload = await syncRes.json();
        const dbUser = (payload.user ?? payload) as any;

        const isDbPower = dbUser.tier === 'power' || dbUser.role === 'admin' || dbUser.role === 'owner';
        const isActuallyInsider = isDbPower || (dbUser.isInsider ?? dbUser.is_insider ?? false) || dbUser.tier === 'pro';
        const fullUser: User = {
          id: dbUser.id ?? supabaseUserId,
          username: dbUser.username ?? dbUser.email?.split("@")[0] ?? "gebruiker",
          email: dbUser.email ?? "",
          tier: dbUser.tier === "power" ? "power" : (isActuallyInsider ? "pro" : ((dbUser.tier as UserTier) ?? "free")),
          role: dbUser.role ?? "user",
          homeAddress: dbUser.homeAddress ?? dbUser.home_address ?? null,
          homeCity: dbUser.homeCity ?? dbUser.home_city ?? null,
          homePostalCode: dbUser.homePostalCode ?? dbUser.home_postal_code ?? null,
          homeLat: dbUser.homeLat ?? dbUser.home_lat ?? null,
          homeLng: dbUser.homeLng ?? dbUser.home_lng ?? null,
          claimedShopIds: dbUser.claimedShopIds ?? [],
          likedShopIds: dbUser.likedShopIds ?? [],
          savedShopIds: dbUser.savedShopIds ?? [],
          routeCount: dbUser.routeCount ?? 0,
          postsThisWeek: dbUser.postsThisWeek ?? 0,
          avatarUrl: dbUser.avatarUrl ?? dbUser.avatar_url ?? null,
          isInsider: isActuallyInsider,
          extraCredits: dbUser.extraCredits ?? dbUser.extra_credits ?? 0,
          consent_marketing: dbUser.consent_marketing ?? false,
          consent_analytical: dbUser.consent_analytical ?? false,
          consent_ads: dbUser.consent_ads ?? false,
          consent_location: dbUser.consent_location ?? false,
        };

        if (AUTH_VERBOSE_LOGS) {
          console.log("[AUTH] user updated", { id: fullUser.id, tier: fullUser.tier, role: fullUser.role });
        }

        // Store full user in a dedicated key to avoid Supabase overwrites
        localStorage.setItem('vintmap-user-profile', JSON.stringify(fullUser));

        // Only update state if data actually changed to prevent re-renders
        setUser(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(fullUser)) {
            return fullUser;
          }
          return prev;
        });
        setTierState(fullUser.tier);
      } catch (error: any) {
        if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
          if (AUTH_VERBOSE_LOGS) console.log("[AUTH] user fetch aborted, staying logged in");
          return;
        }
        console.error("[AUTH] Error fetching user data:", error);
        // Only reset if it's a critical non-abort error
        // setUser(null);
        // setTierState("anonymous");
      } finally {
        (window as any).__authFetchInFlight = false;
      }
    },
    [],
  );

  // Refresh auth state
  const refreshAuth = useCallback(async () => {
    if (AUTH_VERBOSE_LOGS) console.log("[AUTH] refreshAuth called");
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (AUTH_VERBOSE_LOGS) console.log("[AUTH] refreshAuth getSession", { hasSession: !!session });

      if (session?.user && session.access_token) {
        await fetchUserData(session.user.id, session.access_token);
      } else {
        setUser(null);
        setTierState("anonymous");
      }
    } catch (error: any) {
      if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
        if (AUTH_VERBOSE_LOGS) console.log("[AUTH] refreshAuth aborted, staying logged in");
        return;
      }
      console.error("Error refreshing auth:", error);
      // Don't reset user state on generic refresh errors (e.g. network timeout)
      // unless we are sure the session is gone.
      // setUser(null);
      // setTierState("anonymous");
    }
    // Belangrijk: loading state wordt alleen centraal in de init-useEffect beheerd
  }, [fetchUserData]);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      localStorage.removeItem('vintmap-user-profile');
      localStorage.removeItem('vintmap-session'); // Clear this too just in case
      setUser(null);
      setTierState("anonymous");
      // Use replace for a hard navigation and state clear
      window.location.replace('/');
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let initialHandled = false;
    if (AUTH_VERBOSE_LOGS) console.log("[AUTH] init auth effect mounted");

    // 1) Initial session and user verification
    supabase.auth
      .getUser()
      .then(async ({ data: { user }, error }) => {
        if (AUTH_VERBOSE_LOGS) {
          console.log("[AUTH] initial getUser", { hasError: !!error, hasUser: !!user });
        }

        if (error) {
          if ((error as any)?.name === "AbortError" || (error as any)?.message?.includes("aborted")) {
            return;
          }
          console.error("Error getting user context:", error);
          initialHandled = true;
          setLoading(false);
          setUser(null);
          setTierState("anonymous");
          return;
        }

        if (user) {
          if (AUTH_VERBOSE_LOGS) console.log("[AUTH] initial user found");
          initialHandled = true;

          const { data: { session } } = await supabase.auth.getSession();
          const accessToken = session?.access_token;

          setUser(prev => {
            if (prev && prev.id === user.id && prev.avatarUrl) {
              return prev;
            }
            const isActuallyInsider = (user.user_metadata?.is_insider || user.user_metadata?.tier === "pro") || (prev as any)?.isInsider || false;
            return {
              id: user.id,
              email: user.email || "",
              username: user.user_metadata?.username || user.email?.split("@")[0] || "gebruiker",
              tier: isActuallyInsider ? "pro" : (user.user_metadata?.tier || "free"),
              role: user.user_metadata?.role || "user",
              claimedShopIds: prev?.claimedShopIds || [],
              routeCount: prev?.routeCount || 0,
              postsThisWeek: prev?.postsThisWeek || 0,
              homeAddress: prev?.homeAddress,
              avatarUrl: user.user_metadata?.avatar_url || null,
              isInsider: isActuallyInsider
            };
          });

          setLoading(false);
          if (accessToken) {
            fetchUserData(user.id, accessToken);
          }
        } else {
          if (AUTH_VERBOSE_LOGS) console.log("[AUTH] no initial user");
          initialHandled = true;
          setLoading(false);
          setUser(null);
          setTierState("anonymous");
        }
      })
      .catch((error) => {
        if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
          return;
        }
        console.error("Error getting user (catch):", error);
        initialHandled = true;
        setLoading(false);
        setUser(null);
        setTierState("anonymous");
      });

    // 2) Listen for auth changes (PKCE SIGNED_IN, token refresh, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (AUTH_VERBOSE_LOGS) console.log("[AUTH] auth event", event);
      try {
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED" || (event as any) === "INITIAL_SESSION") {
          let effectiveSession = session;

          // Soms bevat het event geen access_token; dan nogmaals getSession doen
          if (!effectiveSession?.access_token || !effectiveSession.user) {
            const { data: { session: currentSession }, error } = await supabase.auth.getSession();
            if (AUTH_VERBOSE_LOGS) console.log("[AUTH] refreshed session in auth event", !!currentSession);
            if (currentSession) {
              effectiveSession = currentSession;
            }
          }

          if (effectiveSession?.user && effectiveSession.access_token) {
            if (AUTH_VERBOSE_LOGS) console.log("[AUTH] syncing user from auth event");
            await fetchUserData(effectiveSession.user.id, effectiveSession.access_token);
            if (!initialHandled) {
              setLoading(false);
              initialHandled = true;
            }
          }
        } else if (event === "SIGNED_OUT" || (event as string) === "SESSION_EXPIRED") {
          localStorage.removeItem('vintmap-user-profile');
          localStorage.removeItem('vintmap-session');
          await supabase.auth.signOut().catch(() => { });
          setUser(null);
          setTierState("anonymous");
          if (!initialHandled) {
            setLoading(false);
            initialHandled = true;
          }
        } else {
          // Other events (SIGNED_UP, etc.)
          if (session?.user && session.access_token) {
            await fetchUserData(session.user.id, session.access_token);
            if (!initialHandled) {
              setLoading(false);
              initialHandled = true;
            }
          } else {
            setUser(null);
            setTierState("anonymous");
            if (!initialHandled) {
              setLoading(false);
              initialHandled = true;
            }
          }
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
        setUser(null);
        setTierState("anonymous");
        if (!initialHandled) {
          setLoading(false);
          initialHandled = true;
        }
      }
    });

    // 3) Optioneel: sessie verlengen bij visibility/focus/interval – ongewijzigd
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user && session.access_token) {
            fetchUserData(session.user.id, session.access_token).catch((err) =>
              console.error("Error refreshing session on visibility change:", err),
            );
          }
        }).catch(err => console.debug("Session check aborted/failed on visibility change", err));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const refreshInterval = setInterval(() => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user && session.access_token) {
          fetchUserData(session.user.id, session.access_token).catch(() => {
            // Silent fail
          });
        }
      }).catch(() => { /* Silent fail */ });
    }, 30 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [fetchUserData]);

  // Legacy setTier for demo purposes (kept for backwards compatibility)
  const setTier = useCallback((newTier: UserTier) => {
    // Only allow setting tier if user is logged in
    if (user && newTier !== "anonymous") {
      // Update tier via API
      (async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        await fetch(`/api/users/${user.id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ tier: newTier })
        });
      })().catch(console.error);
    }
  }, [user]);

  const isLoggedIn = tier !== "anonymous" && user !== null;
  const isPower = tier === "power" || user?.role === "admin" || user?.role === "owner";

  const limits = (tier === "pro" || tier === "power") ? PRO_TIER_LIMITS : FREE_TIER_LIMITS;

  const canCreateRoute = isLoggedIn && (isPower || (user?.routeCount ?? 0) < limits.maxRoutes);
  const canPost = isLoggedIn && (isPower || (user?.postsThisWeek ?? 0) < limits.maxPostsPerWeek);
  const canUseHomeFeature = tier === "pro" || isPower;
  const remainingRoutes = isPower ? 999 : Math.max(0, limits.maxRoutes - (user?.routeCount ?? 0));
  const maxStopsPerRoute = isPower ? 50 : limits.maxStopsPerRoute;
  const remainingPosts = isPower ? 99 : Math.max(0, limits.maxPostsPerWeek - (user?.postsThisWeek ?? 0));

  const claimShop = useCallback((shopId: string) => {
    if (!user) return;
    setUser(prev => prev ? {
      ...prev,
      claimedShopIds: [...prev.claimedShopIds, shopId]
    } : null);
  }, [user]);

  const unclaimShop = useCallback((shopId: string) => {
    if (!user) return;
    setUser(prev => prev ? {
      ...prev,
      claimedShopIds: prev.claimedShopIds.filter(id => id !== shopId)
    } : null);
  }, [user]);

  const isShopOwner = useCallback((shopId: string) => {
    return user?.claimedShopIds.includes(shopId) ?? false;
  }, [user]);

  const getClaimPrice = useCallback((shopCount: number) => {
    return 19.95;
  }, []);

  const setHomeAddress = useCallback(async (address: string, details?: { city?: string; postalCode?: string; lat?: number; lng?: number }) => {
    if (!user || (tier !== "pro" && !isPower)) return;

    // Optimistic update - only update what we have in User type (assuming homeAddress is there)
    // If User type has other fields, we should update them too, but for now just address is visible usually
    setUser(prev => prev ? { ...prev, homeAddress: address } : null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error("No session");

      const body: any = { homeAddress: address };
      if (details?.city) body.homeCity = details.city;
      if (details?.postalCode) body.homePostalCode = details.postalCode;
      if (details?.lat) body.homeLat = details.lat;
      if (details?.lng) body.homeLng = details.lng;

      await fetch(`/api/users/${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      // Refresh to ensure sync
      // await fetchUserData(user.id, token); 
    } catch (error) {
      console.error("Failed to save home address:", error);
      // Revert optimism if needed (complex without history)
    }
  }, [user, tier]);

  // Always render the Provider, even during loading
  // This prevents "useAuth must be used within an AuthProvider" errors
  return (
    <AuthContext.Provider value={{
      user,
      tier,
      setTier,
      isLoggedIn,
      canCreateRoute,
      canPost,
      canUseHomeFeature,
      isInsider: user?.isInsider || tier === "pro" || tier === "power" || user?.role === "admin" || false,
      remainingRoutes,
      maxStopsPerRoute,
      remainingPosts,
      claimShop,
      unclaimShop,
      isShopOwner,
      getClaimPrice,
      setHomeAddress,
      refreshAuth,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export async function getToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || null;
  } catch (error: any) {
    if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
      return null;
    }
    console.error("[AUTH] getToken failed:", error);
    return null;
  }
}
